import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Box, Link, SxProps, Typography } from '@mui/material';
import { CampaignStatusChip } from 'components/CampaignStatus';
import { CampaignStatus } from 'services/campaign/types';
import { textChannelByChannelValue } from 'services/campaign/constants';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { MetaDataBar } from 'components/MetaDataBar';
import { CampaignTeamMembers } from '../subpages/components/CampaignTeamMembers';
import { ChatBubbleOutline } from '@mui/icons-material';
import { LiveStatusIndicatorV2 } from 'common/ui';
import { UserOrganizationSubscriptionItem } from 'modules/campaigns/types/UserOrganizationSubscriptionItem';
import { Channels } from 'generated-graphql/types';

const formatDate = (date: string) =>
  format(new Date(date), "HH:mm 'on' dd/MM/yy");

const container: SxProps = {
  display: 'flex',
  alignItems: 'center',
  mt: 1
};

type Props = {
  status: CampaignStatus;
  totalProspectsCount?: number;
  messagesProcessedCount?: number;
  createdAt?: string;
  candidateBatchId?: string | null;
  launchScheduleDateTime?: string | null;
  channel?: Channels | null | undefined;
  location?: string;
  externalId?: string | null;
  userName: string;
  userOrganizations: UserOrganizationSubscriptionItem[];
  openSubscribersModal: () => void;
  isCampaignOwner: boolean;
  hasSubscriptionError: boolean;
  onReschedule?: () => void;
};

export const CampaignMetaData: React.FC<Props> = ({
  launchScheduleDateTime,
  status,
  totalProspectsCount,
  messagesProcessedCount,
  channel,
  location,
  userName,
  userOrganizations,
  openSubscribersModal,
  isCampaignOwner,
  hasSubscriptionError,
  externalId,
  onReschedule
}) => {
  const isScheduled = status === CampaignStatus.SCHEDULED;

  const items = useMemo(() => {
    const userOrganizationWithSubscription = userOrganizations.filter(
      (userOrganization) =>
        !!userOrganization.user.campaignSubscriptions?.items.length
    );

    return [
      {
        content: (
          <CampaignStatusChip
            status={status}
            totalProspectsCount={totalProspectsCount}
            messagesProcessedCount={messagesProcessedCount}
          />
        )
      },
      {
        content: externalId ? externalId : null
      },
      {
        content: location,
        icon: <LocationOnIcon fontSize="small" />
      },
      {
        content: `Created by ${userName}`
      },
      {
        content: channel ? textChannelByChannelValue[channel] : '',
        icon: <ChatBubbleOutline fontSize="small" />
      },
      {
        content:
          userOrganizationWithSubscription.length > 0 ? (
            <CampaignTeamMembers
              userOrganizations={userOrganizationWithSubscription}
              onClick={openSubscribersModal}
              isCampaignOwner={isCampaignOwner}
            />
          ) : null
      },
      {
        content:
          isScheduled && launchScheduleDateTime ? (
            <Typography component="span">
              Launch scheduled for {formatDate(launchScheduleDateTime)}
              {onReschedule && (
                <>
                  {' '}
                  <Link
                    component="button"
                    onClick={onReschedule}
                    sx={{ verticalAlign: 'baseline' }}
                  >
                    Reschedule
                  </Link>
                </>
              )}
            </Typography>
          ) : null
      },
      {
        content: (
          <LiveStatusIndicatorV2
            isLive={status !== CampaignStatus.ARCHIVED && !hasSubscriptionError}
          />
        )
      }
    ].filter((item) => !!item.content);
  }, [
    channel,
    location,
    messagesProcessedCount,
    status,
    totalProspectsCount,
    userName,
    userOrganizations,
    isCampaignOwner,
    isScheduled,
    launchScheduleDateTime,
    onReschedule,
    hasSubscriptionError,
    externalId,
    openSubscribersModal
  ]);

  return (
    <Box sx={container}>
      <MetaDataBar items={items} />
    </Box>
  );
};
